import {divIcon} from 'leaflet';
import {useCallback, useMemo} from 'react';
import {Marker as LeafetMarker, Popup, useMap} from 'react-leaflet';

import {MAP_DEFAULTS} from '../../utils/constants';

import {MapCardContainer} from './common';
import {makeMarkerVariantFactory} from './createMarkerVariant';

import type {GeoFeatureProperties} from '../../functions/createGeoFeatures';
import type {LeafletMouseEvent, Map} from 'leaflet';
import type {PointFeature, ClusterFeature} from 'supercluster';
import type Supercluster from 'supercluster';

const markerClasses = 'bg-yellow text-black rounded-full size-6';
const spanClasses = 'text-[0.75em] leading-3 font-semibold';

const createMarkerVariant = makeMarkerVariantFactory();
const clusterMarkers = [
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>1</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>2</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>3</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>4</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>5</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>6</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>7</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>8</span>,
  }),
  createMarkerVariant({
    className: markerClasses,
    children: <span className={spanClasses}>9+</span>,
  }),
];

type ClusterPinProps<P extends GeoFeatureProperties> = {
  cluster: ClusterFeature<P>;
  sc: Supercluster<P, P>;
  maxZoom: number;
  children?: (leaves: PointFeature<P>[], map: Map) => React.ReactNode;
};
export function ClusterPin<P extends GeoFeatureProperties>(
  props: ClusterPinProps<P>
): React.JSX.Element {
  const map = useMap();
  const clusterId = props.cluster.properties.cluster_id;

  const handleExpansionClick = useCallback(
    (e: LeafletMouseEvent): void => {
      // only attempt to zoom in if the current zoom level is less than maxZoom
      if (map.getZoom() >= props.maxZoom) {
        return;
      }

      // get the zoom level at which the current cluster expands, using SuperCluster library
      const clusterExpansionZoom = props.sc.getClusterExpansionZoom(clusterId);

      // set the view (zoom + pan) on single-clicking a cluster
      map.flyTo(e.latlng, clusterExpansionZoom, {
        duration: MAP_DEFAULTS.animDuration,
      });
    },
    [clusterId, map, props.maxZoom, props.sc]
  );

  // all markers within the cluster
  const leaves = useMemo(() => {
    return props.sc.getLeaves(clusterId, Infinity);
  }, [clusterId, props.sc]);

  const markerIdx = Math.min(props.cluster.properties.point_count - 1, 8);

  return (
    <LeafetMarker
      key={`marker-${props.cluster.properties.cluster_id}`}
      icon={divIcon({html: clusterMarkers[markerIdx]})}
      position={{
        lat: props.cluster.geometry.coordinates[1],
        lng: props.cluster.geometry.coordinates[0],
      }}
      eventHandlers={{
        contextmenu: handleExpansionClick,
      }}
    >
      {props.children && (
        <Popup
          // keepInView={true}
          // autoPan={true}
          // autoPanPaddingBottomRight={padding}
          closeButton={false}
        >
          <MapCardContainer onClick={() => map.closePopup()}>
            {props.children(leaves, map)}
          </MapCardContainer>
        </Popup>
      )}
    </LeafetMarker>
  );
}

ClusterPin.displayName = 'ClusterPin';
